import { Outlet, useNavigate } from 'react-router-dom';
import { useClerk as useClerkClerk, useUser as useClerkUser } from '@clerk/clerk-react';
import { useClerk as useDevClerk, useUser as useDevUser } from '../contexts/DevAuthContext';
import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  FolderKanban,
  Bell,
  Settings,
  LogOut,
  Upload,
  Plug,
  Calculator,
  Link2,
  Search,
  Keyboard,
  Building2,
  TrendingUp,
} from 'lucide-react';
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
  SidebarLogo,
  useSidebar,
} from './ui/sidebar';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const hasValidClerkKey = clerkPubKey && clerkPubKey.startsWith('pk_') && clerkPubKey.length > 30;

// Use appropriate hooks based on whether we have valid Clerk keys
const useClerk = hasValidClerkKey ? useClerkClerk : useDevClerk;
const useUser = hasValidClerkKey ? useClerkUser : useDevUser;

const navigation = [
  { label: 'Dashboard', href: '/', icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'Projects', href: '/projects', icon: <FolderKanban className="h-5 w-5" /> },
  { label: 'Providers', href: '/providers', icon: <Building2 className="h-5 w-5" /> },
  { label: 'Alerts', href: '/alerts', icon: <Bell className="h-5 w-5" /> },
  { label: 'Rankings', href: '/rankings', icon: <TrendingUp className="h-5 w-5" /> },
  { label: 'Search Console', href: '/search-console', icon: <Search className="h-5 w-5" /> },
  { label: 'Import', href: '/import', icon: <Upload className="h-5 w-5" /> },
  { label: 'Integrations', href: '/integrations', icon: <Plug className="h-5 w-5" /> },
  { label: 'Formulas', href: '/formulas', icon: <Calculator className="h-5 w-5" /> },
  { label: 'Settings', href: '/settings', icon: <Settings className="h-5 w-5" /> },
];

function LayoutContent() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const { showHelp, setShowHelp } = useKeyboardShortcuts({
    onToggleSidebar: toggleSidebar,
    enabled: true,
  });

  return (
    <div className="flex h-screen w-full bg-background overflow-hidden">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} animate={true}>
        <SidebarBody className="relative justify-between gap-10">
          <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            {/* Logo */}
            <SidebarLogo
              icon={<Link2 className="h-7 w-7 text-primary flex-shrink-0" />}
              label="BacklinkGuard"
            />

            {/* Navigation */}
            <div className="mt-6 flex flex-col gap-1">
              {navigation.map((link, idx) => (
                <SidebarLink key={idx} link={link} />
              ))}
            </div>
          </div>

          {/* Bottom section */}
          <div className="flex flex-col gap-2">
            {/* Keyboard shortcut hint */}
            <SidebarLink
              link={{
                label: 'Shortcuts',
                href: '#',
                icon: <Keyboard className="h-5 w-5" />,
              }}
              onClick={(e) => {
                e.preventDefault();
                setShowHelp(true);
              }}
            />

            {/* User section */}
            <UserSection user={user} onLogout={handleLogout} />
          </div>
        </SidebarBody>
      </Sidebar>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="min-h-full p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Keyboard shortcuts help modal */}
      <KeyboardShortcutsHelp isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}

function UserSection({ user, onLogout }) {
  const { open, animate } = useSidebar();

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-primary-foreground">
            {user?.firstName?.[0] || user?.emailAddresses?.[0]?.emailAddress?.[0]?.toUpperCase() || 'U'}
          </span>
        </div>
        <motion.div
          animate={{
            display: animate ? (open ? 'flex' : 'none') : 'flex',
            opacity: animate ? (open ? 1 : 0) : 1,
          }}
          initial={false}
          transition={{ duration: 0.15 }}
          className="flex-1 min-w-0 flex items-center justify-between"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.firstName || 'User'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.publicMetadata?.plan || 'Free'} Plan
            </p>
          </div>
          <button
            onClick={onLogout}
            className="p-1.5 text-muted-foreground hover:text-destructive rounded-lg hover:bg-muted transition-colors"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </motion.div>
      </div>
    </div>
  );
}

function Layout() {
  return <LayoutContent />;
}

export default Layout;
